import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { useAuth } from './auth/AuthContext';
import { useCompetition } from './CompetitionContext';

const MEETING_SCOPED_BASE_PATHS = new Set([
    'live-timetable', 'draw', 'draw-export-pdf', 'racecards', 'race-cards',
    'competition-admin', 'entry-admin', 'events-admin', 'banding-helper', 'victor-ludorum',
    'system-settings', 'registration-desk', 'racing-dashboard',
    'commentary', 'club-summary'
]);

export function MeetingSwitcher() {
    const { user, getToken, isAdmin } = useAuth();
    const { competitionName } = useCompetition();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(false);

    const formatMeetingMeta = useCallback((meeting) => {
        const dateText = meeting?.regattaDate
            ? new Date(meeting.regattaDate).toLocaleDateString()
            : '';
        const hasCount = Number.isFinite(Number(meeting?.totalCrews));
        const countText = hasCount
            ? `${Number(meeting.totalCrews)} entr${Number(meeting.totalCrews) === 1 ? 'y' : 'ies'}`
            : '';
        const slugText = meeting?.urlSlug ? `/${meeting.urlSlug}` : '';

        const parts = [dateText, countText, slugText].filter(Boolean);
        return parts.length ? parts.join(' • ') : 'No date';
    }, []);

    const getCurrentMeetingInfo = useCallback(() => {
        const segments = String(location.pathname || '').split('/').filter(Boolean);
        if (segments.length >= 2 && MEETING_SCOPED_BASE_PATHS.has(segments[1])) {
            return { slug: segments[0] };
        }
        const params = new URLSearchParams(location.search);
        const meetingId = params.get('meetingId');
        if (meetingId) {
            return { id: meetingId };
        }
        return null;
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!isOpen || !user || !isAdmin) return;

        const fetchMeetings = async () => {
            setLoading(true);
            try {
                const token = getToken();
                const response = await fetch('/api/meeting', {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });

                if (response.ok) {
                    const data = await response.json();
                    setMeetings(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error('Error fetching meetings:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchMeetings();
    }, [isOpen, user, isAdmin, getToken]);

    const handleSelectMeeting = useCallback((meeting) => {
        setIsOpen(false);
        const segments = String(location.pathname || '').split('/').filter(Boolean);
        let targetPath = '/competition-admin';

        // If currently on a meeting-scoped page, stay on that page
        if (segments.length >= 2 && MEETING_SCOPED_BASE_PATHS.has(segments[1])) {
            targetPath = `/${segments[1]}`;
        }

        // Full page load so the current page re-fetches everything for the new meeting.
        if (meeting.urlSlug) {
            window.location.assign(`/${meeting.urlSlug}${targetPath}`);
        } else {
            window.location.assign(`${targetPath}?meetingId=${meeting.id}`);
        }
    }, [location.pathname]);

    const currentMeeting = getCurrentMeetingInfo();

    const isMeetingActive = useCallback((meeting) => (
        (currentMeeting?.slug && currentMeeting.slug.toLowerCase() === (meeting.urlSlug || '').toLowerCase()) ||
        (currentMeeting?.id && currentMeeting.id === String(meeting.id))
    ), [currentMeeting]);

    if (!isAdmin) {
        return null;
    }

    const displayName = competitionName || 'Select regatta';

    return (
        <Dropdown isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} className="regatta-meeting-switcher">
            <DropdownToggle caret className="regatta-meeting-toggle">
                <span className="regatta-meeting-label">{displayName}</span>
            </DropdownToggle>
            <DropdownMenu className="regatta-meeting-menu">
                {loading ? (
                    <DropdownItem disabled className="regatta-meeting-state">Loading...</DropdownItem>
                ) : meetings.length === 0 ? (
                    <DropdownItem disabled className="regatta-meeting-state">No regattas available</DropdownItem>
                ) : (
                    meetings.map((meeting) => (
                        <DropdownItem
                            key={meeting.id}
                            onClick={() => handleSelectMeeting(meeting)}
                            active={isMeetingActive(meeting)}
                            className={`regatta-meeting-option${isMeetingActive(meeting) ? ' is-active' : ''}`}
                        >
                            <div className="regatta-meeting-item">
                                <div className="regatta-meeting-item-name">{meeting.regattaName || 'Unnamed regatta'}</div>
                                <div className="regatta-meeting-item-meta">{formatMeetingMeta(meeting)}</div>
                            </div>
                            {isMeetingActive(meeting) && (
                                <span className="regatta-meeting-check" aria-hidden="true">✓</span>
                            )}
                        </DropdownItem>
                    ))
                )}
                <DropdownItem divider />
                <DropdownItem
                    tag={Link}
                    to="/signup"
                    onClick={() => setIsOpen(false)}
                    className="regatta-meeting-add-link"
                >
                    + Add a regatta
                </DropdownItem>
            </DropdownMenu>
        </Dropdown>
    );
}
